import { useCallback, useEffect, useMemo, useState } from 'react'
import { Layers, Save } from 'lucide-react'
import {
  Bidi, Card, CardHeader, ErrorState, LoadingState, NoPermissionState, NumberField,
  useLocale,
} from '@hk/ui'
import {
  fetchLinenStandards, formatCount, may, nameIn, saveLinenStandard, translateError,
  type LinenStandardBoard, type LinenStandardKind,
} from '@hk/core'
import { STRINGS } from '../i18n/strings'

/* ============================================================================
   W-107 · WHAT EACH KIND OF ROOM NEEDS FOR EACH KIND OF CLEAN.  CQ-136.

   ── WHY THIS SCREEN EXISTS ───────────────────────────────────────────────────────────
   The hotel's linen standards have been OURS since Sprint 40 — two bath towels, two hand
   towels, one bath mat, from the approved pack's own M-03 screen, doubled for a room of
   four — and every place they appear says so in writing. The client's answer to CQ-136 on
   17 September was *"start from ours, they correct it"*.

   **UNTIL THIS SCREEN THERE WAS NOWHERE TO DO THE CORRECTING.** The numbers could be
   changed by a developer editing a seed file, which is not a hotel correcting its own
   standard — it is a hotel asking us to.

   ── ONE KIND OF WORK AT A TIME ───────────────────────────────────────────────────────
   The data is a cube: room type × kind of work × item. Drawing all three at once is a
   spreadsheet nobody can read, so the kind of work is a CHOICE and the grid below it is
   items down the side and room types across — which is exactly how a hotel says it out
   loud: *"for a departure, a room for four takes four bath towels."*

   ── EVERY CELL IS A NUMBER, AND AN EMPTY ONE MEANS NOTHING IS CARRIED ────────────────
   Not a zero. `LinenRules` has said since Sprint 40 that a standard of zero is the row's
   absence said twice, and that rows at zero would put "0 bath mats" on the screen an
   attendant reads in a corridor beside the towels that matter. Clearing a cell REMOVES the
   standard, and the placeholder says so rather than showing a nought.
   ========================================================================= */

export function LinenStandards({ propertyId }: { propertyId: string }) {
  const { locale } = useLocale()
  const t = STRINGS[locale]

  const [board, setBoard] = useState<LinenStandardBoard | null>(null)
  const [taskTypeId, setTaskTypeId] = useState<string>('')
  const [error, setError] = useState<unknown>(null)
  const [saving, setSaving] = useState<string | null>(null)
  /* The typed value per cell while it is being edited. **Null is an empty box**,
     which is not the same as a zero — see `NumberField`. */
  const [draft, setDraft] = useState<Record<string, number | null>>({})

  /* **THE GRANT GATES THE REQUEST, NOT ONLY THE DRAWING.**

     The permission check sat below the effect, so a person without the grant still asked
     the server for the hotel's standards and was refused — nothing leaked, because the
     service checks too, but a screen that fires a request it knows will be refused is one
     that fills an audit log with noise and teaches nobody anything. Found by a test that
     asked whether the fetch had happened at all. */
  const allowed = may('admin.linenStandards')

  const load = useCallback(async () => {
    if (!allowed) return
    setError(null)
    try {
      const next = await fetchLinenStandards(propertyId)
      setBoard(next)
      setTaskTypeId((current) => current || next.taskTypes[0]?.id || '')
    } catch (caught: unknown) {
      setError(caught)
    }
  }, [propertyId, allowed])

  useEffect(() => { void load() }, [load])

  /* THE CELL'S ADDRESS. The triple IS the identity of a standard — the server keys on it
     and saving the same triple twice is a correction rather than a second row. */
  const key = (roomTypeId: string, stockItemId: string) =>
    `${taskTypeId}:${roomTypeId}:${stockItemId}`

  const stored = useMemo(() => {
    const map = new Map<string, number>()
    for (const line of board?.lines ?? [])
      map.set(`${line.taskTypeId}:${line.roomTypeId}:${line.stockItemId}`, line.quantity)
    return map
  }, [board])

  if (!allowed) return <NoPermissionState />
  if (error !== null && board === null) {
    return <ErrorState body={translateError(error, locale).message} onRetry={() => void load()} />
  }
  if (board === null) return <LoadingState />

  async function commit(roomTypeId: string, stockItemId: string, typed: number | null) {
    const cell = key(roomTypeId, stockItemId)

    /* **AN EMPTY BOX IS A REMOVAL, NOT A ZERO.** The server takes 0 and deletes the row;
       this is the only place in the product where "nothing" and "none" are the same act. */
    const quantity = typed ?? 0
    if (!Number.isInteger(quantity) || quantity < 0 || quantity > 99) return

    if ((stored.get(cell) ?? 0) === quantity) {
      setDraft((d) => { const next = { ...d }; delete next[cell]; return next })
      return
    }

    setSaving(cell); setError(null)
    try {
      setBoard(await saveLinenStandard(propertyId, {
        roomTypeId, taskTypeId, stockItemId, quantity,
      }))
      setDraft((d) => { const next = { ...d }; delete next[cell]; return next })
    } catch (caught: unknown) {
      setError(caught)
    } finally {
      setSaving(null)
    }
  }

  return (
    <div className="flex flex-col gap-3.5">
      <Card>
        <CardHeader title={t.linenTitle} icon={<Layers className="size-4" />} />
        <div className="flex flex-col gap-3 p-3.5">
          <p className="max-w-[70ch] text-[12.5px] leading-relaxed text-muted-foreground">
            {t.linenIntro}
          </p>

          {/* **OURS UNTIL THEY SAY OTHERWISE, AND THE SCREEN SAYS SO.** The numbers a hotel
              finds here on its first morning were taken from the approved design pack and
              doubled by room size; nothing in the product treats them as authority, and a
              screen that did not admit that would be quoting the client back to itself. */}
          <p className="max-w-[70ch] rounded-hk border border-border bg-muted/40 px-3 py-2
                        text-[12px] leading-relaxed text-muted-foreground">
            {t.linenOurs}
          </p>

          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[12px] font-medium">{t.linenForWork}</span>
            {board.taskTypes.map((kind) => (
              <button
                key={kind.id}
                type="button"
                onClick={() => setTaskTypeId(kind.id)}
                className={`rounded-hk border px-2.5 py-1 text-xs cursor-pointer
                  ${kind.id === taskTypeId
                    ? 'border-[var(--hk-primary)] bg-[var(--hk-primary)] text-white'
                    : 'border-border hover:bg-muted'}`}
              >
                <Bidi>{nameIn(locale, kind.name, kind.nameAr)}</Bidi>
              </button>
            ))}
          </div>
        </div>
      </Card>

      {error !== null && (
        <ErrorState body={translateError(error, locale).message} onRetry={() => setError(null)} />
      )}

      <Card>
        <CardHeader
          title={t.linenGrid}
          icon={<Save className="size-4" />}
        />
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-[12.5px]">
            <thead>
              <tr className="border-b border-border text-[11px] text-muted-foreground">
                <th className="p-2 text-start font-medium">{t.linenItem}</th>
                {board.roomTypes.map((type) => (
                  <th key={type.id} className="p-2 text-start font-medium">
                    <Bidi>{nameIn(locale, type.name, type.nameAr)}</Bidi>
                    {/* **HOW MANY PEOPLE IT SLEEPS, IN THE HEADING.** "Four towels" is a
                        guess without it and an obvious answer with it. */}
                    <div className="font-normal">{sleeps(type, locale, t)}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {board.items.map((item) => (
                <tr key={item.stockItemId} className="border-b border-border last:border-0">
                  <td className="p-2">
                    <Bidi>{nameIn(locale, item.name, item.nameAr)}</Bidi>
                    <span className="ms-1.5 font-mono text-[11px] text-muted-foreground">
                      {item.code}
                    </span>
                  </td>
                  {board.roomTypes.map((type) => {
                    const cell = key(type.id, item.stockItemId)
                    const value = cell in draft ? draft[cell]! : (stored.get(cell) ?? null)
                    return (
                      <td key={type.id} className="p-2">
                        {/* **CQ-139 — ٤ AND 4 ARE THE SAME NUMBER.** A browser's own
                            `type="number"` refuses Arabic digits at the keyboard, so a
                            person reading an Arabic screen full of ٤s could not type one
                            back. `NumberField` folds both. */}
                        <NumberField
                          min={0}
                          max={99}
                          value={value}
                          disabled={saving === cell}
                          aria-label={`${item.code} · ${type.code}`}
                          placeholder={t.linenNone}
                          onChange={(next) => setDraft((d) => ({ ...d, [cell]: next }))}
                          onCommit={(next) => void commit(type.id, item.stockItemId, next)}
                          className="h-8 w-20 px-2 text-[13px]"
                        />
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="border-t border-border px-3.5 py-2 text-[11.5px] leading-relaxed
                      text-muted-foreground">
          {t.linenNote}
        </p>
      </Card>
    </div>
  )
}

/** "Sleeps 4" under a room type's name, or nothing where the type has no capacity. */
function sleeps(type: LinenStandardKind, locale: 'en' | 'ar', t: typeof STRINGS['en']) {
  if (type.capacity === null) return null
  return t.linenSleeps.replace('{count}', formatCount(type.capacity, locale))
}
